import { useEffect, useState } from 'react'
import type { CounterDef } from '../content/company'
import { TBC } from '../content/tbc'

export interface Counter {
  label: string
  shown: string
}

function format(value: number, def: CounterDef): string {
  return def.prefix + Math.round(value).toLocaleString('en-ZA') + def.suffix
}

/**
 * Runs the home-page figures up from zero on an ease-out cubic, once per mount.
 *
 * A definition with `target: null` has no figure to publish yet and renders the
 * placeholder instead — the animation still runs for the rest.
 */
export function useCounters(defs: CounterDef[], durationMs: number): Counter[] {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!defs.some((d) => d.target !== null)) return
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      setProgress(1)
      return
    }
    const start = performance.now()
    const duration = Math.max(1, durationMs)
    let raf = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      setProgress(1 - Math.pow(1 - t, 3))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [defs, durationMs])

  return defs.map((d) => ({
    label: d.label,
    shown: d.target === null ? TBC : format(d.target * progress, d),
  }))
}
